"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DesignSystem = void 0;
/*
 * Copyright (c) 2023 Discover Financial Services
 * Licensed under MIT License. See License.txt in the project root for license information
 */
const node_1 = require("./common/node");
const layers_1 = require("./layers/layers");
const atoms_1 = require("./atoms/atoms");
const molecules_1 = require("./molecules/molecules");
const organisms_1 = require("./organisms/organisms");
const code_1 = require("./code/code");
const shade_1 = require("./common/shade");
const logger_1 = require("./util/logger");
const log = new logger_1.Logger("ds");
/**
 * A design system
 * @category Main
 */
class DesignSystem extends node_1.Node {
    constructor(name, themeBuilder, opts) {
        super(name);
        this.dsShades = {};
        opts = opts || {};
        this.themeBuilder = themeBuilder;
        this.atoms = new atoms_1.Atoms(this);
        this.molecules = new molecules_1.Molecules(this);
        this.organisms = new organisms_1.Organisms(this);
        this.layers = new layers_1.Layers(this);
        this.code = new code_1.Code(this);
        const now = Date.now();
        this.metadata = {
            sample: opts.sample || false,
            time: {
                createdInMs: now,
                lastUpdateInMs: now,
            },
            colors: {
                primary: "",
                secondary: "",
                tertiary: "",
                background: "",
            },
        };
    }
    clone() {
        throw new Error("TODO");
    }
    getNode(key) {
        return this.dsNodes[key];
    }
    registerNode(node) {
        this.dsNodes[node.key] = node;
    }
    getShade(key) {
        const shade = this.dsShades[key];
        if (shade)
            return shade;
        return shade_1.Shade.getCoreShade(key);
    }
    findShade(key) {
        const shade = this.getShade(key);
        if (shade)
            return shade;
        throw new Error(`Shade '${key}' was not found`);
    }
    registerShade(key, shade) {
        log.debug(`Registering shade ${key}`);
        this.dsShades[key] = shade;
    }
    getCSSVarGroup(node) {
        return this.code.cssGenerator.getVarGroup(node.key);
    }
    listProperties() {
        const props = [];
        this._addProperties(props);
        return props;
    }
    getTimeOfCreationInMs() {
        return this.metadata.time.createdInMs;
    }
    getTimeOfLastUpdateInMs() {
        return this.metadata.time.lastUpdateInMs;
    }
    isSample() {
        return this.metadata.sample;
    }
    setIsSample(sample) {
        this.metadata.sample = sample;
    }
    updateMetadata() {
        const theme = this.atoms.colorThemes.getDefaultTheme();
        if (theme) {
            const primary = theme.primary.getValue();
            if (primary)
                this.metadata.colors.primary = primary.getHexOrRGBA();
            const secondary = theme.secondary.getValue();
            if (secondary)
                this.metadata.colors.secondary = secondary.getHexOrRGBA();
            const tertiary = theme.tertiary.getValue();
            if (tertiary)
                this.metadata.colors.tertiary = tertiary.getHexOrRGBA();
            const background = theme.lightModeBackground.getValue();
            if (background)
                this.metadata.colors.background = background.primary.getHexOrRGBA();
        }
        this.metadata.time.lastUpdateInMs = Date.now();
    }
    store() {
        return __awaiter(this, void 0, void 0, function* () {
            this.updateMetadata();
            yield this.themeBuilder.storage.set(this.name, this.serialize());
        });
    }
    /**
     * Make copy of this design system under a new name.
     * @param newName The name of the new design system copy to make.
     * @returns A copy of this design system with a new name.
     */
    copy(newName) {
        return __awaiter(this, void 0, void 0, function* () {
            const ds = new DesignSystem(newName, this.themeBuilder);
            ds.deserialize(this.serialize());
            yield ds.store();
            return ds;
        });
    }
    serialize() {
        const obj = super.serialize();
        obj.atoms = this.atoms.serialize();
        obj.molecules = this.molecules.serialize();
        obj.organisms = this.organisms.serialize();
        obj.layers = this.layers.serialize();
        obj.metadata = this.metadata;
        return obj;
    }
    deserialize(obj) {
        if (!obj)
            return;
        super.deserialize(obj);
        this.atoms.deserialize(obj.atoms);
        this.molecules.deserialize(obj.molecules);
        this.organisms.deserialize(obj.organisms);
        this.layers.deserialize(obj.layers);
        this.metadata = obj.metadata;
        this.code.generate();
    }
}
exports.DesignSystem = DesignSystem;
